import {
  PROTECTED,
  PROTECTED_DASHBOARD,
  SIGN_IN,
} from '@/lib/constants/routes';

// I deep link delle email di Previxio puntano dentro l'area protetta. Il
// parametro `redirect` di /sign-in accetta SOLO percorsi relativi sotto questo
// prefisso: whitelist per prefisso, così nessuna stringa esterna (assoluta,
// protocol-relative o con schema) può diventare una destinazione post login.
const PROTECTED_PATH_PREFIX = `${PROTECTED}/`;

// Un backslash è equivalente a `/` per diversi browser: `/\evil.test`
// verrebbe letto come URL protocol-relative, quindi non è mai accettabile.
const BACKSLASH = '\\';

// `/protected/../sign-up` passa la whitelist per prefisso, ma il browser
// risolve i `..` e atterra FUORI dall'area protetta — e le risalite si
// scrivono in troppi modi per enumerarle (`..`, `%2e%2e`, `%2e.`, `.%2e`).
// Il percorso viene quindi NORMALIZZATO con lo stesso parser del browser e la
// whitelist si applica al risultato: ciò che il parser risolve fuori
// dall'area protetta non ha più il prefisso e viene rifiutato.
const PLACEHOLDER_ORIGIN = 'http://redirect.invalid';

const REDIRECT_PARAM = 'redirect';

export interface SignInRedirectLocation {
  pathname: string;
  searchStr?: string;
}

function normalizeRelativeTarget(redirect: string): string | null {
  try {
    const url = new URL(redirect, PLACEHOLDER_ORIGIN);

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    // Il parser rifiuta alcune stringhe (es. `http://[`): non sono percorsi.
    return null;
  }
}

export function resolvePostLoginTarget(redirect: unknown): string {
  // Difesa in profondità: lo schema della route scarta già i valori non
  // stringa che il parser della search può produrre (number, boolean, array),
  // ma il parametro qui è `unknown` e la funzione è chiamabile anche fuori dal
  // router (interceptor del client API), quindi il tipo si verifica comunque.
  if (typeof redirect !== 'string') return PROTECTED_DASHBOARD;
  if (!redirect) return PROTECTED_DASHBOARD;
  if (redirect.includes(BACKSLASH)) return PROTECTED_DASHBOARD;

  const target = normalizeRelativeTarget(redirect);
  if (!target?.startsWith(PROTECTED_PATH_PREFIX)) return PROTECTED_DASHBOARD;

  return target;
}

export function buildSignInSearchFromLocation(
  location: SignInRedirectLocation
): { redirect?: string } {
  const { pathname, searchStr = '' } = location;

  if (!pathname.startsWith(PROTECTED_PATH_PREFIX)) return {};
  // La dashboard è già la destinazione predefinita: portarsela dietro
  // nell'URL di login non aggiunge nulla e allunga il link.
  if (pathname === PROTECTED_DASHBOARD) return {};

  return { redirect: `${pathname}${searchStr}` };
}

/**
 * URL di login che conserva la posizione corrente, per chi non passa dal
 * router (l'interceptor del client API su 401 fa un `location.replace`).
 */
export function buildSignInUrl(location: SignInRedirectLocation): string {
  const { redirect } = buildSignInSearchFromLocation(location);
  if (!redirect) return SIGN_IN;

  const params = new URLSearchParams({ [REDIRECT_PARAM]: redirect });

  return `${SIGN_IN}?${params.toString()}`;
}
